import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { admin } from '../../api'
import { formatBytes, parseBytes } from '../../format'

export default function AdminShares() {
  const queryClient = useQueryClient()
  const shares = useQuery({ queryKey: ['admin', 'shares'], queryFn: admin.shares })
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users })
  const nodes = useQuery({ queryKey: ['admin', 'nodes'], queryFn: admin.nodes })

  const [userId, setUserId] = useState('')
  const [nodeId, setNodeId] = useState('')

  // With a single node there is nothing to choose, so stop asking. Adding a
  // second one brings the picker back without any setting to remember.
  const soleNodeId = nodes.data?.length === 1 ? nodes.data[0]!.id : null
  const effectiveNodeId = soleNodeId ?? nodeId
  const [path, setPath] = useState('')
  const [label, setLabel] = useState('')
  const [permission, setPermission] = useState<'read' | 'write'>('write')
  const [quota, setQuota] = useState('')
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'shares'] })

  const create = useMutation({
    mutationFn: () => {
      const quotaBytes = quota.trim() === '' ? null : parseBytes(quota)
      if (quota.trim() !== '' && quotaBytes === null) {
        throw new Error('용량은 "50GB" 같은 형식으로 입력하세요')
      }
      return admin.createShare({
        userId,
        nodeId: effectiveNodeId,
        path: path.trim(),
        label: label.trim(),
        permission,
        quotaBytes,
      })
    },
    onSuccess: () => {
      setPath('')
      setLabel('')
      setQuota('')
      setError(null)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      admin.updateShare(id, body),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => admin.deleteShare(id),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate()
  }

  const ready = (users.data?.length ?? 0) > 0 && (nodes.data?.length ?? 0) > 0

  return (
    <section>
      {!ready ? (
        <p className="muted">공유를 만들려면 사용자와 노드가 각각 하나 이상 필요합니다.</p>
      ) : (
        <form className="row-form" onSubmit={submit}>
          <select value={userId} onChange={(event) => setUserId(event.target.value)} required>
            <option value="">사용자 선택</option>
            {users.data?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName} ({user.email})
              </option>
            ))}
          </select>
          {!soleNodeId && (
            <select value={nodeId} onChange={(event) => setNodeId(event.target.value)} required>
              <option value="">노드 선택</option>
              {nodes.data?.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
          )}
          <input
            placeholder="폴더 경로 (예: /family/photos)"
            value={path}
            onChange={(event) => setPath(event.target.value)}
            required
          />
          <input
            placeholder="표시 이름 (선택)"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
          <select
            value={permission}
            onChange={(event) => setPermission(event.target.value as 'read' | 'write')}
          >
            <option value="write">읽기·쓰기</option>
            <option value="read">읽기 전용</option>
          </select>
          <input
            placeholder="용량 제한 (비우면 무제한)"
            value={quota}
            onChange={(event) => setQuota(event.target.value)}
          />
          <button type="submit" className="primary">
            공유 추가
          </button>
        </form>
      )}

      {error && (
        <p className="error banner" onClick={() => setError(null)}>
          {error}
        </p>
      )}

      <p className="muted small">
        경로는 에이전트의 <code>/data</code> 기준입니다. 폴더가 아직 없으면 사용자가 처음 접속할 때
        빈 폴더로 보이며, 파일을 올리면 그때 만들어집니다.
      </p>

      <table className="entries">
        <thead>
          <tr>
            <th>사용자</th>
            {!soleNodeId && <th>노드</th>}
            <th>경로</th>
            <th>표시 이름</th>
            <th>권한</th>
            <th className="num">사용량</th>
            <th className="actions" />
          </tr>
        </thead>
        <tbody>
          {shares.data?.length === 0 && (
            <tr>
              <td colSpan={soleNodeId ? 6 : 7} className="muted center">
                공유가 없습니다.
              </td>
            </tr>
          )}
          {shares.data?.map((share) => (
            <tr key={share.id}>
              <td>
                {share.userDisplayName}
                <br />
                <span className="muted small mono">{share.userEmail}</span>
              </td>
              {!soleNodeId && <td>{share.nodeName}</td>}
              <td className="mono small">{share.path}</td>
              <td>{share.label || '-'}</td>
              <td>
                <select
                  value={share.permission}
                  onChange={(event) =>
                    update.mutate({ id: share.id, body: { permission: event.target.value } })
                  }
                >
                  <option value="write">읽기·쓰기</option>
                  <option value="read">읽기 전용</option>
                </select>
              </td>
              <td className="num">
                {formatBytes(share.usedBytes)}
                {share.quotaBytes !== null && ` / ${formatBytes(share.quotaBytes)}`}
              </td>
              <td className="actions">
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    const input = window.prompt(
                      '용량 제한 (비우면 무제한)',
                      share.quotaBytes === null ? '' : formatBytes(share.quotaBytes),
                    )
                    if (input === null) return
                    const quotaBytes = input.trim() === '' ? null : parseBytes(input)
                    if (input.trim() !== '' && quotaBytes === null) {
                      setError('용량은 "50GB" 같은 형식으로 입력하세요')
                      return
                    }
                    update.mutate({ id: share.id, body: { quotaBytes } })
                  }}
                >
                  용량 변경
                </button>
                <button
                  type="button"
                  className="link danger"
                  onClick={() => {
                    if (!window.confirm('이 공유를 삭제할까요? 실제 파일은 그대로 남습니다.')) return
                    remove.mutate(share.id)
                  }}
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
